import { useState, useRef } from 'react'
import { useAuth } from '../../context/AuthContext'
import { supabase } from '../../supabaseClient'
import { Camera, Save, HelpCircle, Trash2, BookOpen, Bot, Building2, Image, Calculator, Plus, Lock } from 'lucide-react'
import OnboardingTour from '../../components/Onboarding/OnboardingTour'
import { getRoleConfig } from '../../utils/roleUtils'

const Tooltip = ({ text }) => (
  <div className="relative inline-flex group ml-1.5 align-middle">
    <HelpCircle size={14} className="text-gray-600 hover:text-violet-400 cursor-help transition-colors" />
    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-gray-800 border border-gray-700 rounded-xl px-3 py-2.5 text-xs text-gray-300 leading-relaxed opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 shadow-xl">
      {text}
      <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-700" />
    </div>
  </div>
)

const inputCls = "w-full bg-gray-900 border border-gray-800 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-violet-500 transition-colors text-sm"

/* Estrae solo il nome da frasi come "chiamami Meth", "mi chiamo Luca", ecc. */
const extractName = (raw) => {
  const prefixes = [
    /^chiamami\s+/i,
    /^call me\s+/i,
    /^mi chiamo\s+/i,
    /^sono\s+/i,
    /^il mio nome è\s+/i,
    /^my name is\s+/i,
    /^mi puoi chiamare\s+/i,
    /^puoi chiamarmi\s+/i,
    /^name[:\s]+/i,
    /^nome[:\s]+/i,
  ]
  let result = raw.trim()
  for (const re of prefixes) {
    result = result.replace(re, '')
  }
  return result.trim()
}

// ─── ATECO coefficient lookup ─────────────────────────────────────────────────
// Source: Allegato 2, Legge 145/2018 (Legge di Bilancio 2019), art. 1 c. 10
// che ha sostituito Allegato 4 della Legge 190/2014 — in vigore ad oggi.
// Riferimento ufficiale: agenziaentrate.gov.it / normattiva.it
const getAtecoInfo = (code) => {
  if (!code?.trim()) return null
  const div = parseInt(code.trim().replace(',', '.'))
  if (isNaN(div)) return null
  const f = parseFloat(code.trim().replace(',', '.'))

  // ── 40% ──────────────────────────────────────────────────────────────────
  // Industrie alimentari e delle bevande
  if (div >= 10 && div <= 11) return { coeff: 40, cat: 'Industrie alimentari e bevande' }
  // Alloggio e ristorazione
  if (div >= 55 && div <= 56) return { coeff: 40, cat: 'Alloggio e ristorazione' }
  // Commercio ambulante alimentare
  if (div === 47 && f >= 47.81 && f < 47.82) return { coeff: 40, cat: 'Commercio ambulante — alimentari e bevande' }
  // Commercio all'ingrosso e al dettaglio (45, 46.2–46.9, 47 escluso ambulante 47.82+)
  if (div === 45) return { coeff: 40, cat: 'Commercio di autoveicoli' }
  if (div === 46 && f >= 46.2) return { coeff: 40, cat: 'Commercio all\'ingrosso' }
  if (div === 47 && f < 47.82) return { coeff: 40, cat: 'Commercio al dettaglio' }

  // ── 54% ──────────────────────────────────────────────────────────────────
  // Commercio ambulante altri prodotti (47.82–47.89)
  if (div === 47 && f >= 47.82) return { coeff: 54, cat: 'Commercio ambulante — altri prodotti' }

  // ── 62% ──────────────────────────────────────────────────────────────────
  // Intermediari del commercio (46.1)
  if (div === 46 && f >= 46.1 && f < 46.2) return { coeff: 62, cat: 'Intermediari del commercio' }

  // ── 78% ──────────────────────────────────────────────────────────────────
  // Attività professionali, scientifiche, tecniche, sanitarie, istruzione, finanziarie
  // (Sezioni K, M, P, Q — ATECO 64–66, 69–75, 85, 86–88)
  if (div >= 64 && div <= 66) return { coeff: 78, cat: 'Servizi finanziari e assicurativi' }
  if (div >= 69 && div <= 75) return { coeff: 78, cat: 'Attività professionali, scientifiche e tecniche' }
  if (div === 85)              return { coeff: 78, cat: 'Istruzione' }
  if (div >= 86 && div <= 88) return { coeff: 78, cat: 'Sanità e assistenza sociale' }

  // ── 86% ──────────────────────────────────────────────────────────────────
  // Costruzioni e attività immobiliari (Sezioni F, L — ATECO 41–43, 68)
  if (div >= 41 && div <= 43) return { coeff: 86, cat: 'Costruzioni' }
  if (div === 68)              return { coeff: 86, cat: 'Attività immobiliari' }

  // ── 67% — TUTTE LE ALTRE ATTIVITÀ (catch-all) ────────────────────────────
  // Include: agricoltura (01-03), estrattivo (05-09), manifatturiero n.a.s. (12-33),
  // energia/rifiuti (35-39), trasporti (49-53), IT/media/video/software (58-63),
  // noleggio e servizi alle imprese (77-82), arte/sport/intrattenimento (90-99),
  // altri servizi (94-96)
  return { coeff: 67, cat: 'Altre attività economiche' }
}

const calcForfettario = ({ coeff, aliquota, ritenuta, base = 1000 }) => {
  const imponibile = base * coeff / 100
  const imposta    = imponibile * aliquota / 100
  const inps       = imponibile * 0.2623
  const totale     = imposta + inps
  const ritenutaAmt = ritenuta ? base * 0.20 : 0
  return {
    imponibile,
    imposta,
    inps,
    totale,
    percTotale: totale / base * 100,
    ritenutaAmt,
    daAggiungere: Math.max(0, totale - ritenutaAmt),
  }
}

const Impostazioni = () => {
  const { user, profile, refreshProfile, getDisplayName, signOut } = useAuth()
  const [fullName, setFullName] = useState(profile?.full_name || '')
  const [nickname, setNickname] = useState(profile?.nickname || '')
  const [preference, setPreference] = useState(profile?.display_name_preference || 'nickname')
  const [ruoloCr, setRuoloCr] = useState(profile?.ruolo_creativo || '')
  const [bio, setBio] = useState(profile?.bio || '')
  const [linkedin, setLinkedin] = useState(profile?.linkedin_url || '')
  const [website, setWebsite] = useState(profile?.website_url || '')
  const [portfolio, setPortfolio] = useState(profile?.portfolio_url || '')
  const [emailContatto, setEmailContatto] = useState(profile?.email_contatto || '')
  const [disponibile, setDisponibile] = useState(profile?.disponibile ?? true)
  const [botAddressAs, setBotAddressAs] = useState(profile?.bot_address_as || '')
  // Fiscal / billing fields
  const [ragioneSociale, setRagioneSociale] = useState(profile?.ragione_sociale || '')
  const [piva, setPiva] = useState(profile?.piva || '')
  const [codFiscale, setCodFiscale] = useState(profile?.codice_fiscale || '')
  const [regimeFiscale, setRegimeFiscale] = useState(profile?.regime_fiscale || 'ordinario')
  const [ivaPerc, setIvaPerc] = useState(profile?.iva_percentuale?.toString() || '22')
  const [indirizzo, setIndirizzo] = useState(profile?.indirizzo || '')
  const [cap, setCap] = useState(profile?.cap || '')
  const [citta, setCitta] = useState(profile?.citta || '')
  const [provincia, setProvincia] = useState(profile?.provincia || '')
  const [logoUrl, setLogoUrl] = useState(profile?.logo_url || null)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const logoInputRef = useRef(null)
  const [tariffaOraria, setTariffaOraria] = useState(profile?.tariffa_oraria?.toString() || '')
  const [serviziTipici, setServiziTipici] = useState(
    Array.isArray(profile?.servizi_tipici) ? profile.servizi_tipici : []
  )
  const [codiceAteco, setCodiceAteco] = useState(profile?.codice_ateco || '')
  const [aliquotaSostitutiva, setAliquotaSostitutiva] = useState(profile?.aliquota_sostitutiva?.toString() || '15')
  const [ritenutaAcconto, setRitenutaAcconto] = useState(profile?.ritenuta_acconto ?? false)
  const [loading, setLoading] = useState(false)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [message, setMessage] = useState(null)
  const [error, setError] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [showTour, setShowTour] = useState(false)
  // Cambio password
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pwLoading, setPwLoading] = useState(false)
  const [pwMessage, setPwMessage] = useState(null)
  const [pwError, setPwError] = useState(null)

  const handleChangePassword = async () => {
    setPwError(null); setPwMessage(null)
    if (!currentPassword) { setPwError('Inserisci la password attuale.'); return }
    if (newPassword.length < 8) { setPwError('La nuova password deve avere almeno 8 caratteri.'); return }
    if (newPassword !== confirmPassword) { setPwError('Le due nuove password non coincidono.'); return }
    setPwLoading(true)
    // Verifica la password attuale ri-autenticando
    const { error: signErr } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: currentPassword,
    })
    if (signErr) {
      setPwError('La password attuale non è corretta.')
      setPwLoading(false)
      return
    }
    const { error: e } = await supabase.auth.updateUser({ password: newPassword })
    if (e) {
      setPwError(e.message.includes('should be different') ? 'La nuova password deve essere diversa da quella attuale.' : e.message)
    } else {
      setPwMessage('Password aggiornata con successo.')
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('')
    }
    setPwLoading(false)
  }
  const fileInputRef = useRef(null)

  const avatarUrl = profile?.avatar_url ? `${profile.avatar_url}?t=${Date.now()}` : null

  const handleSave = async () => {
    setLoading(true); setError(null); setMessage(null)
    const { error } = await supabase.from('profiles').update({
      full_name: fullName,
      nickname,
      display_name_preference: preference,
      ruolo_creativo: ruoloCr || null,
      bio: bio || null,
      linkedin_url: linkedin || null,
      website_url: website || null,
      portfolio_url: portfolio || null,
      email_contatto: emailContatto || null,
      disponibile,
      bot_address_as: botAddressAs.trim() ? extractName(botAddressAs) || null : null,
      ragione_sociale: ragioneSociale.trim() || null,
      piva: piva.trim() || null,
      codice_fiscale: codFiscale.trim() || null,
      regime_fiscale: regimeFiscale,
      iva_percentuale: regimeFiscale === 'forfettario' ? 0 : (parseFloat(ivaPerc) || 22),
      indirizzo: indirizzo.trim() || null,
      cap: cap.trim() || null,
      citta: citta.trim() || null,
      provincia: provincia.trim().toUpperCase().slice(0, 2) || null,
      logo_url: logoUrl || null,
      tariffa_oraria: parseFloat(tariffaOraria) || null,
      servizi_tipici: serviziTipici.filter(s => s.nome?.trim()),
      codice_ateco: codiceAteco.trim() || null,
      aliquota_sostitutiva: parseInt(aliquotaSostitutiva) || 15,
      ritenuta_acconto: ritenutaAcconto,
    }).eq('id', profile.id)
    if (error) setError(error.message)
    else { await refreshProfile(); setMessage('Profilo aggiornato.') }
    setLoading(false)
  }

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    setUploadingAvatar(true); setError(null)
    const ext = file.name.split('.').pop()
    const path = `${profile.id}/avatar.${ext}`
    const { error: uploadError } = await supabase.storage.from('avatars').upload(path, file, { upsert: true })
    if (uploadError) { setError(uploadError.message); setUploadingAvatar(false); return }
    const { data } = supabase.storage.from('avatars').getPublicUrl(path)
    const { error: updateError } = await supabase.from('profiles').update({ avatar_url: data.publicUrl }).eq('id', profile.id)
    if (updateError) setError(updateError.message)
    else { await refreshProfile(); setMessage('Foto profilo aggiornata.') }
    setUploadingAvatar(false)
  }

  const handleLogoChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    setUploadingLogo(true); setError(null)
    const ext = file.name.split('.').pop()
    const path = `${profile.id}/logo.${ext}`
    const { error: uploadError } = await supabase.storage.from('avatars').upload(path, file, { upsert: true })
    if (uploadError) { setError(uploadError.message); setUploadingLogo(false); return }
    const { data } = supabase.storage.from('avatars').getPublicUrl(path)
    setLogoUrl(data.publicUrl)
    await supabase.from('profiles').update({ logo_url: data.publicUrl }).eq('id', profile.id)
    setUploadingLogo(false)
    setMessage('Logo aggiornato.')
  }

  const handleDeleteAccount = async () => {
    setDeleteLoading(true)
    const { error } = await supabase.rpc('delete_my_account')
    if (error) { setError(error.message); setDeleteLoading(false); return }
    await signOut()
  }

  const TOOLTIP_CREATIVO = "Queste informazioni ci permettono di tenerti nel nostro database creativo. Se in futuro avessimo bisogno di figure professionali per un progetto, potremmo contattarti direttamente. Tutto è completamente opzionale."

  return (
    <div className="max-w-lg">
      <h1 className="text-2xl font-bold text-white mb-8">Impostazioni profilo</h1>

      {/* Avatar */}
      <div className="flex items-center gap-6 mb-8">
        <div className="relative">
          <div className="w-20 h-20 rounded-full bg-violet-600 flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
            {avatarUrl
              ? <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" />
              : <span>{getDisplayName()?.[0]?.toUpperCase() ?? '?'}</span>}
          </div>
          <button onClick={() => fileInputRef.current.click()} disabled={uploadingAvatar}
            className="absolute -bottom-1 -right-1 w-7 h-7 bg-violet-600 hover:bg-violet-500 rounded-full flex items-center justify-center transition-colors">
            <Camera size={13} className="text-white" />
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
        </div>
        <div>
          <p className="text-white font-medium">{getDisplayName()}</p>
          <p className={`text-sm font-medium ${getRoleConfig(profile?.role).className}`}>{getRoleConfig(profile?.role).label}</p>
          {uploadingAvatar && <p className="text-violet-400 text-xs mt-1">Caricamento...</p>}
        </div>
      </div>

      <div className="space-y-5">
        {/* --- Profilo base --- */}
        <div className="space-y-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Profilo</p>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Nome completo</label>
            <input type="text" value={fullName} onChange={e => setFullName(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Nickname</label>
            <input type="text" value={nickname} onChange={e => setNickname(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-2">Mostra nella sidebar</label>
            <div className="flex gap-3">
              {['nickname', 'full_name'].map(p => (
                <button key={p} onClick={() => setPreference(p)}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium border transition-colors ${preference === p ? 'bg-violet-600 border-violet-600 text-white' : 'bg-gray-900 border-gray-800 text-gray-400 hover:border-gray-600'}`}>
                  {p === 'nickname' ? 'Nickname' : 'Nome completo'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* --- Profilo creativo (opzionale) --- */}
        <div className="space-y-4 pt-2">
          <div className="flex items-center gap-2">
            <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Profilo creativo</p>
            <span className="text-xs text-gray-600 bg-gray-800 px-2 py-0.5 rounded-full">opzionale</span>
            <Tooltip text={TOOLTIP_CREATIVO} />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Ruolo creativo</label>
            <input value={ruoloCr} onChange={e => setRuoloCr(e.target.value)} placeholder="es. Motion Designer, Colorist, Editor..." className={inputCls} />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Bio</label>
            <textarea value={bio} onChange={e => setBio(e.target.value)} rows={3} placeholder="Raccontati brevemente..."
              className="w-full bg-gray-900 border border-gray-800 rounded-lg px-4 py-3 text-white text-sm focus:outline-none focus:border-violet-500 transition-colors resize-none" />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Email di contatto</label>
            <input type="email" value={emailContatto} onChange={e => setEmailContatto(e.target.value)} placeholder="email@esempio.com" className={inputCls} />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">LinkedIn</label>
            <input value={linkedin} onChange={e => setLinkedin(e.target.value)} placeholder="https://linkedin.com/in/..." className={inputCls} />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Sito web</label>
            <input value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://..." className={inputCls} />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-1">Portfolio</label>
            <input value={portfolio} onChange={e => setPortfolio(e.target.value)} placeholder="https://..." className={inputCls} />
          </div>

          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <div onClick={() => setDisponibile(v => !v)}
                className={`w-10 h-6 rounded-full transition-colors relative flex-shrink-0 ${disponibile ? 'bg-violet-600' : 'bg-gray-700'}`}>
                <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${disponibile ? 'translate-x-5' : 'translate-x-1'}`} />
              </div>
              <span className="text-sm text-gray-300">Disponibile per progetti</span>
            </label>
          </div>
        </div>

        {/* --- Dati fiscali --- */}
        <div className="space-y-4 pt-2 border-t border-gray-800 mt-2">
          <div className="flex items-center gap-2 pt-4">
            <Building2 size={14} className="text-violet-400" />
            <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Dati fiscali</p>
            <span className="text-xs text-gray-600 bg-gray-800 px-2 py-0.5 rounded-full">per preventivi e PDF</span>
          </div>

          {/* Logo aziendale */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Logo aziendale</label>
            <div className="flex items-center gap-4">
              <div
                onClick={() => logoInputRef.current.click()}
                className="w-24 h-16 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center cursor-pointer hover:border-violet-500 transition-colors overflow-hidden flex-shrink-0"
              >
                {logoUrl
                  ? <img src={`${logoUrl}?t=${Date.now()}`} alt="logo" className="w-full h-full object-contain p-1" />
                  : <Image size={22} className="text-gray-600" />}
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => logoInputRef.current.click()}
                  disabled={uploadingLogo}
                  className="text-sm text-violet-400 hover:text-violet-300 border border-violet-900/50 hover:border-violet-700 px-3 py-1.5 rounded-lg transition-colors"
                >
                  {uploadingLogo ? 'Caricamento...' : logoUrl ? 'Cambia logo' : 'Carica logo'}
                </button>
                {logoUrl && (
                  <button
                    type="button"
                    onClick={() => setLogoUrl(null)}
                    className="ml-2 text-sm text-gray-600 hover:text-red-400 transition-colors"
                  >
                    Rimuovi
                  </button>
                )}
                <p className="text-xs text-gray-600 mt-1">PNG o JPG, max 2 MB. Appare nei PDF dei preventivi.</p>
                <input ref={logoInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={handleLogoChange} className="hidden" />
              </div>
            </div>
          </div>

          {/* Ragione sociale */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Ragione sociale / Nome commerciale</label>
            <input value={ragioneSociale} onChange={e => setRagioneSociale(e.target.value)} placeholder="es. Plutonic Studio di Mario Rossi" className={inputCls} />
          </div>

          {/* P.IVA + CF */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Partita IVA</label>
              <input value={piva} onChange={e => setPiva(e.target.value)} placeholder="IT12345678901" className={inputCls} />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Codice Fiscale</label>
              <input value={codFiscale} onChange={e => setCodFiscale(e.target.value)} placeholder="RSSMRA80A01H501Z" className={inputCls} />
            </div>
          </div>

          {/* Codice ATECO */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Codice ATECO</label>
            <input
              value={codiceAteco}
              onChange={e => setCodiceAteco(e.target.value)}
              placeholder="es. 59.12.09"
              className={`${inputCls} max-w-xs`}
            />
            {getAtecoInfo(codiceAteco) && (
              <p className="text-xs text-violet-400 mt-1">
                → {getAtecoInfo(codiceAteco).cat} · coefficiente <strong>{getAtecoInfo(codiceAteco).coeff}%</strong>
              </p>
            )}
          </div>

          {/* Regime fiscale */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Regime fiscale</label>
            <div className="flex gap-2 flex-wrap">
              {[
                { value: 'ordinario', label: 'Ordinario' },
                { value: 'forfettario', label: 'Forfettario' },
                { value: 'altro', label: 'Altro' },
              ].map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setRegimeFiscale(opt.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    regimeFiscale === opt.value
                      ? 'bg-violet-600 border-violet-600 text-white'
                      : 'bg-gray-900 border-gray-800 text-gray-400 hover:border-gray-600'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* IVA % — solo se non forfettario */}
          {regimeFiscale !== 'forfettario' && (
            <div>
              <label className="block text-sm text-gray-400 mb-1">Aliquota IVA (%)</label>
              <input
                type="number"
                value={ivaPerc}
                onChange={e => setIvaPerc(e.target.value)}
                min="0"
                max="100"
                step="0.5"
                className={`${inputCls} max-w-xs`}
                placeholder="22"
              />
              <p className="text-xs text-gray-600 mt-1">Standard: 22% — modifica solo se hai un'aliquota agevolata.</p>
            </div>
          )}

          {/* Campi extra per forfettario */}
          {regimeFiscale === 'forfettario' && (<>
            <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl px-4 py-3">
              <p className="text-sm text-violet-300">In regime forfettario l'IVA non viene applicata. I PDF includeranno la dicitura di legge automaticamente.</p>
            </div>

            {/* Aliquota imposta sostitutiva */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Imposta sostitutiva</label>
              <div className="flex gap-2">
                {[
                  { value: '5',  label: '5%',  note: 'Startup (primi 5 anni)' },
                  { value: '15', label: '15%', note: 'Regime ordinario' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setAliquotaSostitutiva(opt.value)}
                    className={`flex-1 py-2.5 px-3 rounded-lg text-sm border transition-colors text-left ${
                      aliquotaSostitutiva === opt.value
                        ? 'bg-violet-600 border-violet-600 text-white'
                        : 'bg-gray-900 border-gray-800 text-gray-400 hover:border-gray-600'
                    }`}
                  >
                    <span className="font-bold">{opt.label}</span>
                    <span className={`block text-xs mt-0.5 ${aliquotaSostitutiva === opt.value ? 'text-violet-200' : 'text-gray-600'}`}>{opt.note}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Ritenuta d'acconto */}
            <div>
              <label className="flex items-start gap-3 cursor-pointer">
                <div
                  onClick={() => setRitenutaAcconto(v => !v)}
                  className={`mt-0.5 w-10 h-6 rounded-full transition-colors relative flex-shrink-0 ${ritenutaAcconto ? 'bg-violet-600' : 'bg-gray-700'}`}
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${ritenutaAcconto ? 'translate-x-5' : 'translate-x-1'}`} />
                </div>
                <div>
                  <span className="text-sm text-gray-300">Soggetta a ritenuta d'acconto (20%)</span>
                  <p className="text-xs text-gray-600 mt-0.5">
                    I tuoi clienti business tratterranno il 20% e te ne pagheranno l'80%. Appare nel PDF con la dicitura di legge.
                  </p>
                </div>
              </label>
            </div>

            {/* ── Calcolatrice fiscale ── */}
            {(() => {
              const info = getAtecoInfo(codiceAteco)
              if (!info) return (
                <div className="border border-dashed border-gray-700 rounded-xl px-4 py-4 text-center">
                  <Calculator size={18} className="text-gray-600 mx-auto mb-1" />
                  <p className="text-xs text-gray-600">Inserisci il codice ATECO per vedere la stima fiscale</p>
                </div>
              )
              const c = calcForfettario({
                coeff: info.coeff,
                aliquota: parseInt(aliquotaSostitutiva),
                ritenuta: ritenutaAcconto,
              })
              return (
                <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Calculator size={14} className="text-violet-400" />
                    <p className="text-sm font-semibold text-white">Stima fiscale — ogni €1.000 fatturati</p>
                    <span className="ml-auto text-xs text-gray-500">coefficiente {info.coeff}%</span>
                  </div>

                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div>
                      <p className="text-xs text-gray-500">Reddito imponibile</p>
                      <p className="text-white font-medium">€{c.imponibile.toFixed(0)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Imposta sostitutiva ({aliquotaSostitutiva}%)</p>
                      <p className="text-amber-400 font-medium">€{c.imposta.toFixed(0)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">INPS gestione separata</p>
                      <p className="text-amber-400 font-medium">€{c.inps.toFixed(0)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Totale tasse</p>
                      <p className="text-orange-400 font-bold">€{c.totale.toFixed(0)} ({c.percTotale.toFixed(1)}%)</p>
                    </div>
                  </div>

                  {ritenutaAcconto ? (
                    <div className="bg-gray-700/60 rounded-lg px-3 py-2.5 space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Il cliente ti paga subito</span>
                        <span className="text-white font-medium">€{(1000 - c.ritenutaAmt).toFixed(0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Ritenuta copre tasse</span>
                        <span className="text-green-400 font-medium">€{c.ritenutaAmt.toFixed(0)}</span>
                      </div>
                      <div className="flex justify-between border-t border-gray-600 pt-1">
                        <span className="text-gray-300 font-medium">Metti da parte in più</span>
                        <span className="text-violet-400 font-bold">€{c.daAggiungere.toFixed(0)}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-700/60 rounded-lg px-3 py-2 flex justify-between items-center">
                      <span className="text-sm text-gray-300">Metti da parte ogni €1.000</span>
                      <span className="text-orange-400 font-bold text-base">€{c.totale.toFixed(0)}</span>
                    </div>
                  )}
                </div>
              )
            })()}
          </>)}

          {/* Indirizzo */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Indirizzo</label>
            <input value={indirizzo} onChange={e => setIndirizzo(e.target.value)} placeholder="Via Roma 1" className={inputCls} />
          </div>

          {/* CAP + Città + Provincia */}
          <div className="grid grid-cols-6 gap-3">
            <div className="col-span-1">
              <label className="block text-sm text-gray-400 mb-1">CAP</label>
              <input value={cap} onChange={e => setCap(e.target.value)} maxLength={5} placeholder="00100" className={inputCls} />
            </div>
            <div className="col-span-4">
              <label className="block text-sm text-gray-400 mb-1">Città</label>
              <input value={citta} onChange={e => setCitta(e.target.value)} placeholder="Roma" className={inputCls} />
            </div>
            <div className="col-span-1">
              <label className="block text-sm text-gray-400 mb-1">Prov.</label>
              <input value={provincia} onChange={e => setProvincia(e.target.value)} maxLength={2} placeholder="RM" className={inputCls} />
            </div>
          </div>
        </div>

        {/* --- Tariffe e AI preventivi --- */}
        <div className="space-y-4 pt-2 border-t border-gray-800 mt-2">
          <div className="flex items-center gap-2 pt-4">
            <Calculator size={14} className="text-emerald-400" />
            <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Tariffe e AI preventivi</p>
            <span className="text-xs text-gray-600 bg-gray-800 px-2 py-0.5 rounded-full">usato dall'AI per stimare i prezzi</span>
          </div>

          {/* Tariffa oraria */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Tariffa oraria (€/h)</label>
            <input
              type="number"
              value={tariffaOraria}
              onChange={e => setTariffaOraria(e.target.value)}
              min="0"
              step="5"
              placeholder="es. 80"
              className={`${inputCls} max-w-xs`}
            />
            <p className="text-xs text-gray-600 mt-1">Base di calcolo quando l'AI stima ore di lavoro.</p>
          </div>

          {/* Servizi tipici */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Servizi tipici e prezzi</label>
            <div className="space-y-2">
              {serviziTipici.map((s, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input
                    value={s.nome}
                    onChange={e => {
                      const upd = [...serviziTipici]
                      upd[i] = { ...upd[i], nome: e.target.value }
                      setServiziTipici(upd)
                    }}
                    placeholder="Nome servizio"
                    className="flex-1 bg-gray-900 border border-gray-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-violet-500 transition-colors"
                  />
                  <input
                    type="number"
                    value={s.prezzo}
                    onChange={e => {
                      const upd = [...serviziTipici]
                      upd[i] = { ...upd[i], prezzo: e.target.value }
                      setServiziTipici(upd)
                    }}
                    placeholder="€"
                    min="0"
                    className="w-24 bg-gray-900 border border-gray-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-violet-500 transition-colors text-right"
                  />
                  <select
                    value={s.unita || 'flat'}
                    onChange={e => {
                      const upd = [...serviziTipici]
                      upd[i] = { ...upd[i], unita: e.target.value }
                      setServiziTipici(upd)
                    }}
                    className="w-24 bg-gray-900 border border-gray-800 rounded-lg px-2 py-2 text-sm text-white focus:outline-none focus:border-violet-500 transition-colors"
                  >
                    <option value="flat">flat</option>
                    <option value="ora">/ora</option>
                    <option value="giorno">/giorno</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => setServiziTipici(prev => prev.filter((_, j) => j !== i))}
                    className="text-gray-600 hover:text-red-400 transition-colors p-1"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setServiziTipici(prev => [...prev, { nome: '', prezzo: '', unita: 'flat' }])}
              className="mt-2 flex items-center gap-1.5 text-sm text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              <Plus size={14} /> Aggiungi servizio
            </button>
            <p className="text-xs text-gray-600 mt-2">
              L'AI usa questi prezzi come riferimento principale quando genera i preventivi.
              Più dettagli dai, più precisa sarà.
            </p>
          </div>
        </div>

        {/* --- Assistente personale --- */}
        <div className="space-y-4 pt-2 border-t border-gray-800 mt-2">
          <div className="flex items-center gap-2 pt-4">
            <Bot size={14} className="text-indigo-400" />
            <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Assistente personale</p>
          </div>

          {/* Come ti chiamo */}
          <div>
            <label className="block text-sm text-gray-400 mb-2">Come preferisci essere chiamato nelle notifiche?</label>
            {/* Scelta rapida */}
            <div className="flex gap-2 mb-2">
              {[
                { label: 'Nome', value: fullName },
                { label: 'Nickname', value: nickname },
              ].filter(opt => opt.value).map(opt => (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => setBotAddressAs(opt.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs border transition-colors ${
                    botAddressAs === opt.value
                      ? 'bg-indigo-600/20 border-indigo-500 text-indigo-300'
                      : 'border-gray-700 text-gray-500 hover:border-gray-600 hover:text-gray-300'
                  }`}
                >
                  {opt.label} <span className="opacity-60">({opt.value})</span>
                </button>
              ))}
            </div>
            <input
              type="text"
              value={botAddressAs}
              onChange={e => setBotAddressAs(e.target.value)}
              onBlur={e => { const n = extractName(e.target.value); if (n !== e.target.value.trim()) setBotAddressAs(n) }}
              placeholder="es. Meth, Luca, Boss..."
              className={inputCls}
            />
            <p className="text-xs text-gray-600 mt-1">Scrivi solo il nome (es. <span className="text-gray-500">Meth</span>). Se lasci vuoto, il bot non ti chiamerà per nome.</p>
          </div>
        </div>

        {error && <p className="text-red-400 text-sm">{error}</p>}
        {message && <p className="text-green-400 text-sm">{message}</p>}

        <button onClick={handleSave} disabled={loading}
          className="flex items-center gap-2 bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-medium px-6 py-3 rounded-lg transition-colors">
          <Save size={16} />
          {loading ? 'Salvataggio...' : 'Salva modifiche'}
        </button>

        {/* Tour */}
        <div className="pt-6 mt-6 border-t border-gray-800 space-y-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Tour guidato</p>
          <button
            onClick={() => setShowTour(true)}
            className="flex items-center gap-2 text-sm text-violet-400 hover:text-violet-300 border border-violet-900/50 hover:border-violet-700 px-4 py-2.5 rounded-lg transition-colors"
          >
            <BookOpen size={15} />
            Riapri tour di benvenuto
          </button>
        </div>

        {/* Sicurezza — cambio password */}
        <div className="pt-6 mt-6 border-t border-gray-800 space-y-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Sicurezza</p>
          <div className="space-y-3 max-w-md">
            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Password attuale</label>
              <input
                type="password"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                placeholder="La tua password attuale"
                autoComplete="current-password"
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Nuova password</label>
              <input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Almeno 8 caratteri"
                autoComplete="new-password"
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1.5 block">Conferma nuova password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Ripeti la password"
                autoComplete="new-password"
                className={inputCls}
              />
            </div>
            {pwError && <p className="text-red-400 text-sm">{pwError}</p>}
            {pwMessage && <p className="text-green-400 text-sm">{pwMessage}</p>}
            <button
              onClick={handleChangePassword}
              disabled={pwLoading || !currentPassword || !newPassword || !confirmPassword}
              className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 text-white font-medium px-4 py-2.5 rounded-lg transition-colors text-sm border border-gray-700"
            >
              <Lock size={15} />
              {pwLoading ? 'Aggiornamento...' : 'Aggiorna password'}
            </button>
          </div>
        </div>

        {/* Zona pericolosa */}
        <div className="pt-6 mt-6 border-t border-gray-800 space-y-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Zona pericolosa</p>
          {!showDeleteConfirm ? (
            <button onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 text-sm text-red-400 hover:text-red-300 border border-red-900/50 hover:border-red-700 px-4 py-2.5 rounded-lg transition-colors">
              <Trash2 size={15} />
              Elimina account
            </button>
          ) : (
            <div className="bg-red-950/30 border border-red-900/50 rounded-xl p-4 space-y-3">
              <p className="text-red-300 text-sm font-medium">Sei sicuro di voler eliminare il tuo account?</p>
              <p className="text-red-400/70 text-xs">Questa azione è irreversibile. Tutti i tuoi dati verranno cancellati permanentemente.</p>
              <div className="flex gap-3">
                <button onClick={handleDeleteAccount} disabled={deleteLoading}
                  className="flex items-center gap-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
                  <Trash2 size={14} />
                  {deleteLoading ? 'Eliminazione...' : 'Sì, elimina'}
                </button>
                <button onClick={() => setShowDeleteConfirm(false)}
                  className="text-sm text-gray-400 hover:text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors">
                  Annulla
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showTour && (
        <OnboardingTour forceOpen={true} onClose={() => setShowTour(false)} />
      )}
    </div>
  )
}

export default Impostazioni
